import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { GetTrendingDebates } from "../api-requests/Get";
import { VoteOnDebate } from "../api-requests/Updates";
import { getBearerHeaders } from "../helpers/GetBearerHeaders";
import { UserContext } from "../context/UserContext";

interface TrendingDebatesProps {}

interface DebateData {
  id: string;
  title: string;
  category: string;
  totalVotes: number;
  userVote: string | null;
  options: {
    id: string;
    option_text: string;
    votes: number;
    percentage: number;
  }[];
}

const TrendingDebates = ({}: TrendingDebatesProps) => {
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const [debates, setDebates] = useState<DebateData[]>([]);
  const [loading, setLoading] = useState(true);
  const [votingStates, setVotingStates] = useState<{ [key: string]: boolean }>(
    {},
  );

  useEffect(() => {
    const fetchTrendingDebates = async () => {
      try {
        const headers = user?.isloggedin ? getBearerHeaders() : undefined;
        const response = await GetTrendingDebates(headers);
        if (response.success) {
          setDebates(response.debates);
        }
      } catch (error) {
        console.error("Error fetching trending debates:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchTrendingDebates();
  }, [user?.isloggedin]);

  const handleVote = async (debateId: string, optionId: string) => {
    if (!user.isloggedin) {
      navigate("/login");
      return;
    }

    setVotingStates((prev) => ({ ...prev, [debateId]: true }));

    try {
      const headers = getBearerHeaders();
      const response = await VoteOnDebate(debateId, optionId, headers);

      if (response.success) {
        // Refresh the debates data after voting
        const headers = getBearerHeaders();
        const updatedResponse = await GetTrendingDebates(headers);
        if (updatedResponse.success) {
          setDebates(updatedResponse.debates);
        }
      }
    } catch (error) {
      console.error("Error voting on debate:", error);
    } finally {
      setVotingStates((prev) => ({ ...prev, [debateId]: false }));
    }
  };

  if (loading) {
    return (
      <div className="card-wrap">
        <h2 className="text-xl md:text-2xlfont-bold text-white mb-6">
          🔥 Trending Debates
        </h2>
        <div className="space-y-3">
          {[1, 2, 3].map((item) => (
            <div key={item} className="bg-gray-700 rounded-lg p-3 opacity-50">
              <div className="h-4 bg-gray-600 rounded mb-2 animate-pulse"></div>
              <div className="h-3 bg-gray-600 rounded w-3/4 animate-pulse"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="card-wrap">
      <h2 className="text-xl md:text-2xl font-bold text-white mb-6">
        🔥 Trending Debates
      </h2>

      {debates.length > 0 ? (
        <div className="space-y-4">
          {debates.slice(0, 4).map((debate) => {
            const hasVoted =
              debate.userVote !== null && debate.userVote !== undefined;
            return (
              <div
                key={debate.id}
                className="card-container cursor-pointer group"
                onClick={() => navigate(`/debate/${debate.id}`)}
              >
                <div className="flex items-start justify-between mb-3">
                  <h3 className="text-white font-semibold text-sm leading-tight group-hover:text-blue-400 transition-colors flex-1">
                    {debate.title}
                  </h3>
                  {hasVoted && (
                    <span className="ml-2 shrink-0 text-green-400 text-xs">
                      ✓ Voted
                    </span>
                  )}
                </div>

                {/* Top 2 options */}
                <div className="space-y-2 mb-3">
                  {debate.options.slice(0, 2).map((option) => {
                    const isVotedOption = debate.userVote === option.id;
                    return (
                      <div
                        key={option.id}
                        className="flex items-center justify-between"
                      >
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!hasVoted) handleVote(debate.id, option.id);
                          }}
                          disabled={votingStates[debate.id] || hasVoted}
                          className={`flex-1 text-left text-xs transition-colors truncate mr-2 ${
                            isVotedOption
                              ? "text-green-400 font-semibold"
                              : hasVoted
                                ? "text-gray-400 cursor-default"
                                : "text-gray-300 hover:text-white disabled:opacity-50"
                          }`}
                        >
                          {isVotedOption && "✓ "}
                          {option.option_text}
                        </button>
                        <span
                          className={`text-xs font-semibold ${
                            isVotedOption ? "text-green-400" : "text-blue-400"
                          }`}
                        >
                          {option.votes}
                        </span>
                      </div>
                    );
                  })}
                </div>

                {/* Stats */}
                <div className="flex justify-between items-center text-xs text-gray-400">
                  <span>{debate.totalVotes} total votes</span>
                  <button
                    onClick={() => navigate(`/debate/${debate.id}`)}
                    className="text-blue-400 hover:text-blue-300 transition-colors"
                  >
                    View Full Debate →
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-gray-400 text-center py-8">
          <p>No trending debates at the moment.</p>
        </div>
      )}

      <button
        onClick={() => navigate("/debates")}
        className="w-full mt-6 text-gray-400 hover:text-gray-300 transition-colors"
      >
        View All Debates &rarr;
      </button>
    </div>
  );
};

export default TrendingDebates;
